import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_COMMUNICATION_API ?? 'http://localhost:8164';

export type CommunicationDashboard = {
  tenant_id: string;
  messages_sent_24h: number;
  messages_delivered_24h: number;
  messages_failed_24h: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  active_campaigns: number;
  scheduled_campaigns: number;
  active_templates: number;
  unread_notifications: number;
  active_providers: number;
  channel_breakdown: Record<string, number>;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  category: string;
  action: string;
  message: string;
  actor?: string;
  created_at: string;
};

export type Message = {
  id: string;
  tenant_id: string;
  channel: string;
  type: string;
  subject?: string;
  body: string;
  template_id?: string;
  status: string;
  recipients: Recipient[];
  provider?: string;
  retry_count: number;
  error_message?: string;
  sent_at?: string;
  delivered_at?: string;
  created_at: string;
  updated_at: string;
};

export type Recipient = {
  id?: string;
  email?: string;
  phone?: string;
  user_id?: string;
  name?: string;
  locale?: string;
};

export type Template = {
  id: string;
  tenant_id: string;
  name: string;
  channel: string;
  type: string;
  subject?: string;
  body: string;
  locale: string;
  version: number;
  active: boolean;
  variables?: string[];
  created_at: string;
  updated_at: string;
};

export type Campaign = {
  id: string;
  tenant_id: string;
  name: string;
  channel: string;
  type: string;
  status: string;
  template_id?: string;
  audience_id?: string;
  scheduled_at?: string;
  sent_count: number;
  delivered_count: number;
  open_count: number;
  click_count: number;
  budget_cents?: number;
  created_at: string;
  updated_at: string;
};

export type Notification = {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  body?: string;
  category: string;
  priority: string;
  read: boolean;
  bookmarked: boolean;
  archived: boolean;
  action_url?: string;
  created_at: string;
};

export type ProviderConfig = {
  id: string;
  tenant_id: string;
  channel: string;
  name: string;
  active: boolean;
  priority: number;
  config?: Record<string, unknown>;
};

export type SendMessageRequest = {
  channel: string;
  type?: string;
  template_id?: string;
  subject?: string;
  body?: string;
  recipients: Recipient[];
  variables?: Record<string, unknown>;
  priority?: string;
};

export type CreateTemplateRequest = {
  name: string;
  channel: string;
  type?: string;
  subject?: string;
  body: string;
  locale?: string;
  variables?: string[];
};

export type CreateCampaignRequest = {
  name: string;
  channel: string;
  type?: string;
  template_id?: string;
  audience_id?: string;
  scheduled_at?: string;
  budget_cents?: number;
};

export const communicationApi = {
  dashboard: () => fetchAPI<CommunicationDashboard>(`${BASE}/api/v1/communication/dashboard`),
  sendMessage: (req: SendMessageRequest) =>
    fetchAPI<Message>(`${BASE}/api/v1/messages/send`, { method: 'POST', body: JSON.stringify(req) }),
  listMessages: (limit = 50) =>
    fetchAPI<{ messages: Message[] }>(`${BASE}/api/v1/messages?limit=${limit}`),
  getMessage: (id: string) => fetchAPI<Message>(`${BASE}/api/v1/messages/${id}`),
  listTemplates: () => fetchAPI<{ templates: Template[] }>(`${BASE}/api/v1/templates`),
  createTemplate: (req: CreateTemplateRequest) =>
    fetchAPI<Template>(`${BASE}/api/v1/templates`, { method: 'POST', body: JSON.stringify(req) }),
  listCampaigns: () => fetchAPI<{ campaigns: Campaign[] }>(`${BASE}/api/v1/campaigns`),
  createCampaign: (req: CreateCampaignRequest) =>
    fetchAPI<Campaign>(`${BASE}/api/v1/campaigns`, { method: 'POST', body: JSON.stringify(req) }),
  listNotifications: (userId?: string, unreadOnly = false) => {
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (unreadOnly) params.set('unread', 'true');
    const qs = params.toString();
    return fetchAPI<{ notifications: Notification[] }>(`${BASE}/api/v1/notifications${qs ? `?${qs}` : ''}`);
  },
  markRead: (id: string) =>
    fetchAPI<Notification>(`${BASE}/api/v1/notifications/${id}/read`, { method: 'PUT' }),
  listProviders: () => fetchAPI<{ providers: ProviderConfig[] }>(`${BASE}/api/v1/communication/providers`),
};
